const formatCurrency = (value) => {
  if (typeof value !== "number") return "";
  const formattedValue = new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
  }).format(value);
  return formattedValue;
};

/**
 * Format a number with Dutch locale (comma as decimal separator)
 * Whole numbers are displayed without decimals, decimal numbers with specified decimals
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 2, use 0 for whole numbers only)
 * @returns {string} Formatted number string with comma as decimal separator
 */
const formatNumber = (value) => {
  if (typeof value !== "number") return "";

  const formattedValue = new Intl.NumberFormat("nl-BE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  }).format(value);
  return formattedValue;
};

/**
 * Format a date to DD/MM/YYYY format
 * @param {string|Date|number} dateValue - The date value to format (ISO string, Date object, timestamp)
 * @returns {string} Formatted date string in "DD/MM/YYYY" format
 */
const formatDateDisplay = (dateValue) => {
  if (!dateValue) return "";

  // Try to parse as Date
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Check if a value is a date
 * @param {*} value - The value to check
 * @returns {boolean} True if the value is a Date or ISO date string
 */
const isDateValue = (value) => {
  if (value instanceof Date) {
    return !isNaN(value.getTime());
  }
  if (typeof value === "string") {
    // Check for ISO 8601 date format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}(T|$)/;
    return isoDatePattern.test(value);
  }
  return false;
};

/**
 * Format records with proper number and date formatting
 * @param {Array} records - Array of records to format
 * @returns {Array} Formatted records with numbers and dates properly formatted
 */
const formatRecords = (records) => {
  if (!Array.isArray(records) || records.length === 0) {
    return records;
  }

  return records.map((record) => {
    const formattedRecord = { ...record };

    for (const key in formattedRecord) {
      const value = formattedRecord[key];

      // Format numbers
      if (typeof value === "number" && isFinite(value)) {
        formattedRecord[key] = formatNumber(value);
      }
      // Format dates
      else if (isDateValue(value)) {
        formattedRecord[key] = formatDateDisplay(value);
      }
    }

    return formattedRecord;
  });
};

/**
 * Format a date/datetime value for HTML input elements
 * @param {string|Date|number} dateValue - The date value to format (ISO string, Date object, timestamp, or already formatted string)
 * @param {boolean} includeTime - Whether to include time (default: true for datetime-local inputs)
 * @returns {string} Formatted date string in "YYYY-MM-DDThh:mm" format (or "YYYY-MM-DD" if includeTime is false)
 */
const formatDateForInput = (dateValue, includeTime = true) => {
  if (!dateValue) return "";

  // If it's already in the correct format for datetime-local (YYYY-MM-DDThh:mm), return as-is
  if (typeof dateValue === "string" && includeTime) {
    const datetimeLocalPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
    if (datetimeLocalPattern.test(dateValue)) {
      return dateValue;
    }
  }

  // If it's already in the correct format for date (YYYY-MM-DD), return as-is
  if (typeof dateValue === "string" && !includeTime) {
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (datePattern.test(dateValue)) {
      return dateValue;
    }
  }

  // Try to parse as Date
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "";

  if (includeTime) {
    // For datetime-local inputs: "YYYY-MM-DDThh:mm" using local time
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } else {
    // For date inputs: "YYYY-MM-DD" using local time
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
};

module.exports = {
  formatCurrency,
  formatNumber,
  formatDateDisplay,
  formatDateForInput,
  formatRecords,
};
